import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { dropListProps } from "../../@types/assetTypes/ap";
import ArrowDown from "../../assets/Icons/ArrowDown";
import { useAnchoredMenu } from "../../hooks/useAnchoredMenu";

interface IProps {
  arrayList: dropListProps[];
  changeHandler: (vals: dropListProps) => void;
  value?: dropListProps;
  styleObj?: CSSProperties;
  fullWidth?: boolean;
  disabled?: boolean;
}

const MBODropDown: React.FC<IProps> = (props) => {
  const { arrayList, changeHandler, value, styleObj, fullWidth, disabled } =
    props;

  const [showDropDown, setShowDropDown] = useState<boolean>(false);
  const triggerRef = useRef<HTMLDivElement | null>(null);

  // The menu is rendered in a portal on document.body so it is not clipped by
  // the parent dialog's overflow. It is positioned (fixed) against the trigger.
  const { menuRef, menuStyle } = useAnchoredMenu<HTMLUListElement>(
    triggerRef,
    showDropDown,
  );

  const handleSelect = (val: dropListProps): void => {
    changeHandler(val);
    setShowDropDown(false);
  };

  const toggleDropDown = (e: React.FormEvent<EventTarget>): void => {
    e.preventDefault();
    setShowDropDown(!showDropDown);
  };

  useEffect(() => {
    const checkIfClickedOutside = (event: MouseEvent | TouchEvent): void => {
      const target = event.target as Node;

      const outsideTrigger =
        triggerRef?.current instanceof Node &&
        !triggerRef.current.contains(target);

      // the menu lives in a portal outside the trigger, so check it separately
      const outsideMenu = !menuRef.current?.contains(target);

      if (outsideTrigger && outsideMenu) {
        setShowDropDown(false);
      }
    };

    document.addEventListener("mousedown", checkIfClickedOutside);

    return () => {
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, []);

  return (
    <div className="relative">
      <div
        ref={triggerRef}
        role="presentation"
        id="dropdownMenuButton"
        onClick={disabled ? undefined : (e) => toggleDropDown(e)}
        className={`${fullWidth ? "w-full" : "w-96"} ${
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        } h-10 flex justify-between gap-3 border items-center shadow-custom shadow-blue-200 px-5 py-1 capitalize`}
        style={styleObj}
      >
        <span className="truncate">{value?.name || ""}</span>
        <span
          className={`ml-2 transition-all duration-500 ${
            showDropDown ? "-rotate-180" : ""
          }`}
        >
          {<ArrowDown />}
        </span>
      </div>
      {showDropDown &&
        createPortal(
          <ul
            ref={menuRef}
            style={menuStyle}
            aria-labelledby="dropdownMenuButton"
            className="shadow-custom shadow-blue-200 rounded-md bg-white z-[60] overflow-y-auto overflow-x-hidden"
            // portalled menu still bubbles React events to the dialog; stop
            // mousedown so the dialog's "click outside to close" does not fire
            onMouseDown={(e) => e.stopPropagation()}
          >
            {arrayList?.map((item) => (
              <li
                className="cursor-pointer capitalize px-5 py-1 break-words hover:bg-[#211551] hover:text-white"
                key={item.id}
                onClick={() => {
                  handleSelect(item);
                }}
              >
                {item.name}
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
};

export default MBODropDown;
